import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";
import { uploadFile, MAX_BATCH } from "../../lib/uploadFile";

export default function PortalDropzone({ caseId, onQueued, disabled }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  const send = async (list) => {
    let files = Array.from(list || []);
    if (!files.length) return;
    if (files.length > MAX_BATCH) {
      toast.warning(`Max ${MAX_BATCH} files per batch — uploading the first ${MAX_BATCH}.`);
      files = files.slice(0, MAX_BATCH);
    }
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setBusy({ n: i + 1, total: files.length, name: files[i].name, pct: 0 });
      try {
        const rec = await uploadFile(files[i], { caseId, onProgress: (pct) => setBusy((b) => ({ ...b, pct })) });
        ok += 1;
        onQueued(rec);
      } catch (e) {
        toast.error(`${files[i].name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setBusy(null);
    if (inputRef.current) inputRef.current.value = "";
    if (ok) toast.success(`${ok} document${ok === 1 ? "" : "s"} entered the pipeline — validation → OCR → classification → extraction → review.`);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) send(e.dataTransfer.files); }}
      onClick={() => !busy && !disabled && inputRef.current?.click()}
      data-testid="portal-dropzone"
      className={`border-2 border-dashed rounded-xl px-5 py-6 flex items-center gap-4 cursor-pointer transition-colors duration-200 ${drag ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-white/10 hover:border-[#f59e0b]/50"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <input ref={inputRef} type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.mp3,.wav,.m4a,.mp4,.mov,.webm" onChange={(e) => send(e.target.files)} data-testid="portal-file-input" />
      {busy ? <Loader2 className="w-7 h-7 text-[#f59e0b] animate-spin shrink-0" /> : <UploadCloud className="w-7 h-7 text-[#f59e0b] shrink-0" />}
      <div className="min-w-0">
        <div className="text-sm font-bold text-white">{busy ? `Uploading ${busy.n}/${busy.total} — ${busy.pct}%` : "Drop firm documents here"}</div>
        <div className="text-[11px] text-gray-400 truncate">
          {busy ? busy.name : `PDF, images, audio or video · up to ${MAX_BATCH} per batch · auto-classified against the 18 firm templates`}
        </div>
      </div>
    </div>
  );
}
